import { supabase } from '@/integrations/supabase/client';

export interface AttributeSummary {
  thinking_method_id: string;
  attribute_name: string;
  updated_at: string;
}

export interface AttributeContent extends AttributeSummary {
  attribute_value: string;
}

const PAGE_SIZE = 200;

// Exact count and stable ordering make a short or capped page an export failure.
async function fetchPages<T extends AttributeSummary>(columns: string): Promise<T[]> {
  const rows: T[] = [];
  let expected: number | null = null;
  for (let offset = 0; expected === null || offset < expected; offset += PAGE_SIZE) {
    const { data, error, count } = await supabase
      .from('thinking_method_attributes')
      .select(columns, { count: offset === 0 ? 'exact' : undefined })
      .order('thinking_method_id')
      .order('attribute_name')
      .range(offset, offset + PAGE_SIZE - 1);
    if (error) throw error;
    if (offset === 0) {
      if (count === null) throw new Error('Attribute count is unavailable; export cannot be verified.');
      expected = count;
    }
    const page = (data || []) as unknown as T[];
    if (page.length !== Math.min(PAGE_SIZE, expected! - offset)) {
      throw new Error(`Attribute page is incomplete at row ${offset}; export was cancelled.`);
    }
    rows.push(...page);
  }
  if (rows.length !== expected) throw new Error('Attribute export is incomplete.');
  if (new Set(rows.map(row => `${row.thinking_method_id}\u0000${row.attribute_name}`)).size !== rows.length) {
    throw new Error('Attribute pages overlap; export was cancelled.');
  }
  return rows;
}

export const fetchAttributeSummaries = () =>
  fetchPages<AttributeSummary>('thinking_method_id, attribute_name, updated_at');

export const fetchAttributesForExport = () =>
  fetchPages<AttributeContent>('thinking_method_id, attribute_name, attribute_value, updated_at');

export async function fetchLensesForExport() {
  const rows: Array<{ id: string; name: string; author: string }> = [];
  let expected: number | null = null;
  for (let offset = 0; expected === null || offset < expected; offset += PAGE_SIZE) {
    const { data, error, count } = await supabase.from('thinking_methods')
      .select('id, name, author', { count: offset === 0 ? 'exact' : undefined })
      .order('order_index').order('id').range(offset, offset + PAGE_SIZE - 1);
    if (error) throw error;
    if (offset === 0) {
      if (count === null) throw new Error('Lens count is unavailable; export cannot be verified.');
      expected = count;
    }
    if ((data || []).length !== Math.min(PAGE_SIZE, expected! - offset)) {
      throw new Error(`Lens page is incomplete at row ${offset}; export was cancelled.`);
    }
    rows.push(...(data || []));
  }
  if (new Set(rows.map(row => row.id)).size !== rows.length) {
    throw new Error('Lens pages overlap; export was cancelled.');
  }
  return rows;
}

export async function fetchAttributeContent(methodId: string, name: string): Promise<AttributeContent> {
  const { data, error } = await supabase.from('thinking_method_attributes')
    .select('thinking_method_id, attribute_name, attribute_value, updated_at')
    .eq('thinking_method_id', methodId).eq('attribute_name', name).single();
  if (error) throw error;
  return data;
}
